import { FilterIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import OverlayWindow from 'Components/OverlayWindow';
import { useCreatePopup, useCurrentPopup } from 'Components/Popups';
import Spinner from 'Components/Spinner';
import Tooltip from 'Components/Tooltips';
import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { Accordion } from 'react-accessible-accordion';
import { PreExpandedContext } from './context';
import './style.css';

export function Filters<
  T extends {
    [key: string]:
      | string
      | number
      | boolean
      | undefined
      | Date
      | Array<string | number | boolean>
      | { from?: string; to?: string };
  },
>({
  value,
  isLoading,
  onConfirm,
  children,
  preExpanded: InitialPreExpanded = [],
  className = '',
  locked = [],
}: {
  value: T;
  isLoading: boolean;
  preExpanded?: string[];
  className?: string;
  locked?: (keyof T)[];
  onConfirm: (v: T) => void;
  children: (p: {
    state: T;
    updateField: <Key extends keyof T>(k: Key, value: T[Key]) => void;
    clear: <Key extends keyof T>(k: Key) => void;
  }) => ReactNode;
}) {
  const [state, setState] = useState(value);
  const Popup = useCreatePopup();
  const [preExpanded, setPreExpanded] = useState<string[]>(InitialPreExpanded);

  const updateField = <Key extends keyof T>(k: Key, value: T[Key]) => {
    !locked.includes(k) &&
      setState((_) => ({
        ..._,
        [k]: value,
      }));
  };

  const clear = <Key extends keyof T>(key: Key) => updateField(key, undefined!);

  const clearAll = () => Object.keys(state).forEach(clear);
  const reset = () => setState((_) => value);
  const applyFilters = () => {
    onConfirm(state);
    Popup.close();
  };
  useEffect(reset, [JSON.stringify(value)]);
  const isDirty = !!Object.keys(value).filter(
    (k) => !locked.includes(k) && value[k] !== undefined && value[k] !== '',
  ).length;

  return (
    <>
      <Button
        theme="gray-outline"
        ref={Popup.setAnchor}
        onClick={Popup.toggle}
        className="relative max-w-min"
      >
        <FilterIcon className={`w-5 h-5 ${isDirty ? 'text-green' : ''}`} />
        {isDirty && (
          <div className="w-1.5  h-1.5 bg-[#f09757] rounded-full absolute bottom-1 right-1"></div>
        )}
      </Button>

      <Popup.Component>
        <OverlayWindow
          rounded="rounded-lg"
          paddingSize="py-2"
          size="max-h-[34rem]"
        >
          {isLoading && <Spinner />}
          {!isLoading && (
            <>
              <div className={`overflow-y-scroll w-[min(90vw,_24rem)]`}>
                <PreExpandedContext.Provider
                  value={{ preExpanded, setPreExpanded }}
                >
                  <Accordion preExpanded={preExpanded} allowZeroExpanded>
                    {children && children({ state, updateField, clear })}
                  </Accordion>
                </PreExpandedContext.Provider>
              </div>

              <div className=" flex justify-between border-t gap-2 pt-3 px-2 border-gray-light border-opacity-30">
                <Button
                  className="max-w-min"
                  theme="gray-outline"
                  onClick={clearAll}
                >
                  Clear
                </Button>
                <Button
                  disabled={JSON.stringify(value) === JSON.stringify(state)}
                  className="max-w-min mr-auto"
                  theme="green-outline"
                  onClick={reset}
                >
                  Reset
                </Button>

                <Button
                  disabled={JSON.stringify(value) === JSON.stringify(state)}
                  className="max-w-min"
                  theme="green-fill"
                  onClick={applyFilters}
                >
                  Apply
                </Button>
              </div>
            </>
          )}
        </OverlayWindow>
      </Popup.Component>
    </>
  );
}
